import type { Resource } from '@medplum/fhirtypes';

import { resourceKey } from './resolution-map';

export type PatchOperation =
  | { op: 'add' | 'replace' | 'test'; path: string; value: unknown }
  | { op: 'remove'; path: string };

type EditEntry = { original: Resource; current: Resource; draft?: boolean };

let draftCounter = 0;

/**
 * A draft: a not-yet-created resource editable through the same store,
 * views, and commit path as an existing one. The synthetic id exists only
 * so the store (and React) can key it — commit strips it and POSTs instead
 * of PATCHing. Draft-ness itself is NOT encoded in the id: it is store
 * state, set by registerDraft (usually via the useFhirDraft hook), so
 * drafts can live anywhere — dialogs today, inline page sections later —
 * without id conventions leaking into app code. The caller seeds required
 * context (subject/patient references, sensible defaults); the renderer
 * never guesses.
 */
const DRAFT_ID_PREFIX = 'fhir-draft-';

export function createFhirDraft<T extends Resource>(seed: Omit<T, 'id'>): T {
  draftCounter += 1;
  return { ...seed, id: `${DRAFT_ID_PREFIX}${draftCounter}` } as T;
}

/**
 * A synthetic draft id must never reach the server. Commit uses this as a
 * guard: an entry with a draft id but no draft flag means the draft was
 * already committed and the host was not remounted (see useFhirDraft).
 */
export function hasDraftId(resource: Resource): boolean {
  return resource.id?.startsWith(DRAFT_ID_PREFIX) ?? false;
}

/** The POST payload for a draft: the resource without its synthetic id. */
export function draftPayload(resource: Resource): Resource {
  const payload = { ...stripNulls(resource) };
  delete payload.id;
  return payload;
}

/**
 * The edit-mode state holder: `(original, current)` copies per resource,
 * keyed by `resourceType/id`. Held in a ref-like object with per-key
 * subscriptions (consumed via useSyncExternalStore) so only the view being
 * edited re-renders on a keystroke — a context value would re-render the
 * whole tree.
 *
 * `data` passed to views stays server truth; edits live only here until
 * commit. Diffing to JSONPatch happens at commit time, so re-editing a field
 * is idempotent and cancel is just dropping the copies.
 */
export class FhirEditStore {
  private entries = new Map<string, EditEntry>();
  private keyListeners = new Map<string, Set<() => void>>();
  private storeListeners = new Set<() => void>();
  // Bumped on any change; snapshot for whole-store consumers (dirty count).
  private storeVersion = 0;

  currentFor<T extends Resource>(resource: T): T {
    const key = resourceKey(resource);
    if (!key) return resource;
    return (this.entries.get(key)?.current as T) ?? resource;
  }

  setCurrent<T extends Resource>(resource: T, next: T): void {
    const key = resourceKey(resource);
    if (!key) return;
    const entry = this.entries.get(key);
    this.entries.set(key, {
      original: entry?.original ?? resource,
      current: next,
      draft: entry?.draft,
    });
    this.notify(key);
  }

  /**
   * Mark a resource as a draft (create-on-commit). Idempotent; existing
   * edits survive re-registration. Usually reached via useFhirDraft.
   */
  registerDraft(resource: Resource): void {
    const key = resourceKey(resource);
    if (!key) return;
    const entry = this.entries.get(key);
    this.entries.set(key, {
      original: entry?.original ?? resource,
      current: entry?.current ?? resource,
      draft: true,
    });
    this.notify(key);
  }

  revert(resource: Resource): void {
    const key = resourceKey(resource);
    if (!key) return;
    if (this.entries.delete(key)) this.notify(key);
  }

  isDirty(resource: Resource): boolean {
    const key = resourceKey(resource);
    const entry = key ? this.entries.get(key) : undefined;
    return entry ? !deepEqual(entry.original, entry.current) : false;
  }

  dirtyEntries(): Array<{
    key: string;
    original: Resource;
    current: Resource;
    draft?: boolean;
  }> {
    return [...this.entries.entries()]
      .filter(([, entry]) => !deepEqual(entry.original, entry.current))
      .map(([key, entry]) => ({ key, ...entry }));
  }

  dirtyCount(): number {
    return this.dirtyEntries().length;
  }

  /** Drop every edit (cancel, or post-commit once server truth is refetched). */
  reset(): void {
    const keys = [...this.entries.keys()];
    this.entries.clear();
    for (const key of keys) this.notify(key);
    if (keys.length === 0) this.bump();
  }

  subscribeToResource(resource: Resource, listener: () => void): () => void {
    const key = resourceKey(resource) ?? '';
    const set = this.keyListeners.get(key) ?? new Set();
    set.add(listener);
    this.keyListeners.set(key, set);
    return () => set.delete(listener);
  }

  subscribe = (listener: () => void): (() => void) => {
    this.storeListeners.add(listener);
    return () => this.storeListeners.delete(listener);
  };

  getStoreVersion = (): number => this.storeVersion;

  private notify(key: string): void {
    for (const listener of this.keyListeners.get(key) ?? []) listener();
    this.bump();
  }

  private bump(): void {
    this.storeVersion += 1;
    for (const listener of this.storeListeners) listener();
  }
}

/** Key-order-insensitive: `{a,b}` and `{b,a}` with equal values are equal. */
function deepEqual(a: unknown, b: unknown): boolean {
  return (
    a === b ||
    JSON.stringify(canonicalize(a)) === JSON.stringify(canonicalize(b))
  );
}

function canonicalize(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(canonicalize);
  if (value && typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const out: Record<string, unknown> = {};
    for (const key of Object.keys(record).sort()) {
      out[key] = canonicalize(record[key]);
    }
    return out;
  }
  return value;
}

/**
 * GraphQL responses carry `null` for selected-but-absent fields (FHIR JSON
 * has no nulls) and Apollo stamps `__typename` on every object (the server
 * rejects it as an invalid property). Strip both so diffs compare real
 * content and patch values never leak GraphQL artifacts onto the wire.
 */
function stripNulls<T>(value: T): T {
  if (Array.isArray(value)) {
    return value
      .filter((item) => item !== null && item !== undefined)
      .map(stripNulls) as T;
  }
  if (value && typeof value === 'object') {
    const out: Record<string, unknown> = {};
    for (const [key, entry] of Object.entries(value)) {
      if (entry === null || entry === undefined) continue;
      if (key === '__typename') continue;
      out[key] = stripNulls(entry);
    }
    return out as T;
  }
  return value;
}

const NON_PATCHABLE_KEYS = new Set(['resourceType', 'id', 'meta']);

/**
 * Minimal JSONPatch diff at top-level-field granularity: one replace/add/
 * remove per changed field, plus a `test` on meta.versionId when the
 * original carries one (optimistic locking — the server rejects the patch
 * if the resource changed since it was read). Field-granular ops mean a
 * partial/masked read can never clobber fields the client never saw.
 */
export function diffResourceOps(
  original: Resource,
  current: Resource,
): PatchOperation[] {
  const ops: PatchOperation[] = [];
  const versionId = original.meta?.versionId;
  if (versionId) {
    ops.push({ op: 'test', path: '/meta/versionId', value: versionId });
  }
  const originalRecord = original as unknown as Record<string, unknown>;
  const currentRecord = current as unknown as Record<string, unknown>;
  const keys = new Set([
    ...Object.keys(originalRecord),
    ...Object.keys(currentRecord),
  ]);
  for (const key of keys) {
    if (NON_PATCHABLE_KEYS.has(key)) continue;
    // null (GraphQL "selected but absent") and undefined both mean the field
    // does not exist on the server resource.
    const before = stripNulls(originalRecord[key] ?? undefined);
    const after = stripNulls(currentRecord[key] ?? undefined);
    if (deepEqual(before, after)) continue;
    if (after === undefined) {
      ops.push({ op: 'remove', path: `/${key}` });
    } else if (before === undefined) {
      ops.push({ op: 'add', path: `/${key}`, value: after });
    } else {
      ops.push({ op: 'replace', path: `/${key}`, value: after });
    }
  }
  return ops;
}
